import { fbm, ridged, billow, smoothstepFade } from './noise.js';

export const TERRAIN = {
  size: 880,
  grid: 513,          // 512x512 quads, ~1.72 m vertex spacing
  maxHeight: 76,
  islandStart: 375,
  islandEnd: 435,
  seaDepth: 14,
  roadHalfWidth: 7.5,
  innerCorridor: 9.0,
  outerCorridor: 34.0,
  loop: [
    [0, -290],
    [85, -275],
    [175, -225],
    [245, -135],
    [230, -25],
    [270, 75],
    [215, 185],
    [115, 240],
    [-15, 255],
    [-135, 215],
    [-225, 140],
    [-255, 25],
    [-205, -75],
    [-240, -180],
    [-155, -265],
    [-65, -288]
  ]
};

export const SURFACE = {
  ROCK: 0,
  DIRT: 1,
  GRASS: 2,
  SAND: 3,
  ROAD: 4
};

export const SURFACE_PROPERTIES = {
  [SURFACE.ROCK]:  { name: 'СКАЛА',  grip: 0.84, roll: 0.05, color: [0.38, 0.35, 0.32] },
  [SURFACE.DIRT]:  { name: 'ГРУНТ',  grip: 0.96, roll: 0.04, color: [0.46, 0.32, 0.20] },
  [SURFACE.GRASS]: { name: 'ТРАВА',  grip: 0.82, roll: 0.08, color: [0.24, 0.38, 0.16] },
  [SURFACE.SAND]:  { name: 'ПЕСОК',  grip: 0.74, roll: 0.11, color: [0.65, 0.54, 0.36] },
  [SURFACE.ROAD]:  { name: 'ТРАССА', grip: 1.05, roll: 0.025, color: [0.28, 0.24, 0.18] }
};

export function gripAt(surface) {
  return SURFACE_PROPERTIES[surface]?.grip ?? 0.85;
}

export function rollingResistance(surface) {
  return SURFACE_PROPERTIES[surface]?.roll ?? 0.05;
}

export function surfaceName(surface) {
  return SURFACE_PROPERTIES[surface]?.name ?? '—';
}

function clamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

function hermiteRamp(a, b, x) {
  if (x <= a) return 0;
  if (x >= b) return 1;
  const t = (x - a) / (b - a);
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Spline-based track generation and fast spatial query structure
// ---------------------------------------------------------------------------
function catmullRom2D(p0, p1, p2, p3, t) {
  const t2 = t * t;
  const t3 = t2 * t;
  const f0 = -0.5 * t3 + t2 - 0.5 * t;
  const f1 = 1.5 * t3 - 2.5 * t2 + 1.0;
  const f2 = -1.5 * t3 + 2.0 * t2 + 0.5 * t;
  const f3 = 0.5 * t3 - 0.5 * t2;
  return {
    x: p0[0] * f0 + p1[0] * f1 + p2[0] * f2 + p3[0] * f3,
    z: p0[1] * f0 + p1[1] * f1 + p2[1] * f2 + p3[1] * f3
  };
}

const SPLINE_SAMPLES = 1024;
const trackPoints = [];
const trackTangents = [];
const trackCurvatures = [];

(() => {
  const L = TERRAIN.loop;
  const n = L.length;
  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    const u = (i / SPLINE_SAMPLES) * n;
    const idx1 = Math.floor(u);
    const t = u - idx1;
    const idx0 = (idx1 - 1 + n) % n;
    const idx2 = (idx1 + 1) % n;
    const idx3 = (idx1 + 2) % n;
    const p = catmullRom2D(L[idx0], L[idx1 % n], L[idx2], L[idx3], t);
    trackPoints.push(p);
  }

  // Precompute tangents and curvature
  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    const prev = trackPoints[(i - 1 + SPLINE_SAMPLES) % SPLINE_SAMPLES];
    const next = trackPoints[(i + 1) % SPLINE_SAMPLES];
    const dx = next.x - prev.x;
    const dz = next.z - prev.z;
    const len = Math.hypot(dx, dz) || 1e-4;
    trackTangents.push({ x: dx / len, z: dz / len });
  }

  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    const tPrev = trackTangents[(i - 1 + SPLINE_SAMPLES) % SPLINE_SAMPLES];
    const tNext = trackTangents[(i + 1) % SPLINE_SAMPLES];
    // 2D cross product of consecutive tangents gives curvature sign and magnitude
    const cross = tPrev.x * tNext.z - tPrev.z * tNext.x;
    trackCurvatures.push(cross);
  }
})();

// Fast spatial grid for track distance queries
const GRID_CELL = 44;
const trackGrid = new Map();
const cellKey = (gx, gz) => `${gx}_${gz}`;

(() => {
  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    const p = trackPoints[i];
    const gx = Math.floor(p.x / GRID_CELL);
    const gz = Math.floor(p.z / GRID_CELL);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const key = cellKey(gx + dx, gz + dz);
        let list = trackGrid.get(key);
        if (!list) {
          list = [];
          trackGrid.set(key, list);
        }
        list.push(i);
      }
    }
  }
})();

function distToSegment(px, pz, ax, az, bx, bz) {
  const abx = bx - ax, abz = bz - az;
  const len2 = abx * abx + abz * abz;
  let t = len2 > 0 ? ((px - ax) * abx + (pz - az) * abz) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + abx * t;
  const cz = az + abz * t;
  const dx = px - cx;
  const dz = pz - cz;
  return { dist: Math.hypot(dx, dz), cx, cz, t };
}

// Returns distance to track centerline, signed lateral offset, and nearest index
export function trackQuery(x, z) {
  const gx = Math.floor(x / GRID_CELL);
  const gz = Math.floor(z / GRID_CELL);
  const candidates = trackGrid.get(cellKey(gx, gz));

  let bestDist = Infinity;
  let bestIdx = 0;
  let bestLat = 0;

  if (candidates && candidates.length > 0) {
    for (let c = 0; c < candidates.length; c++) {
      const idx = candidates[c];
      const nextIdx = (idx + 1) % SPLINE_SAMPLES;
      const a = trackPoints[idx];
      const b = trackPoints[nextIdx];
      const res = distToSegment(x, z, a.x, a.z, b.x, b.z);
      if (res.dist < bestDist) {
        bestDist = res.dist;
        bestIdx = idx;
        const tang = trackTangents[idx];
        // Signed lateral offset: positive is right of track, negative is left
        bestLat = (x - res.cx) * -tang.z + (z - res.cz) * tang.x;
      }
    }
  } else {
    // Fallback: search sampled points every 16 steps
    let bi = 0;
    for (let i = 0; i < SPLINE_SAMPLES; i += 16) {
      const p = trackPoints[i];
      const d2 = (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z);
      if (d2 < bestDist) {
        bestDist = d2;
        bi = i;
      }
    }
    bestDist = Infinity;
    for (let i = -16; i <= 16; i++) {
      const idx = (bi + i + SPLINE_SAMPLES) % SPLINE_SAMPLES;
      const nextIdx = (idx + 1) % SPLINE_SAMPLES;
      const a = trackPoints[idx];
      const b = trackPoints[nextIdx];
      const res = distToSegment(x, z, a.x, a.z, b.x, b.z);
      if (res.dist < bestDist) {
        bestDist = res.dist;
        bestIdx = idx;
        const tang = trackTangents[idx];
        bestLat = (x - res.cx) * -tang.z + (z - res.cz) * tang.x;
      }
    }
  }

  return { dist: bestDist, index: bestIdx, lateral: bestLat };
}

export function trackDistance(x, z) {
  return trackQuery(x, z).dist;
}

// ---------------------------------------------------------------------------
// Base wild procedural terrain (without road carve)
// ---------------------------------------------------------------------------
function wildHeight(x, z) {
  const nx = x * 0.0022, nz = z * 0.0022;

  // 1. Continental base: broad rolling elevations
  let h = (fbm(nx, nz, 5, 11) - 0.5) * 2 * 60;

  // 2. Mid-frequency hills
  h += (fbm(nx * 2.8 + 3.2, nz * 2.8 - 1.7, 5, 31) - 0.5) * 2 * 21;

  // 3. Continental elevation offset
  h += 19;

  // 4. Mountain ridges: ridged multifractal masked toward island perimeter
  const r = Math.hypot(x, z);
  const ridgeMask = 1.0 - hermiteRamp(TERRAIN.islandStart * 0.85, TERRAIN.islandStart, r);
  const ridge = ridged(nx * 1.8 - 5.5, nz * 1.8 + 7.1, 5, 59);
  h += Math.pow(ridge, 1.6) * 44 * Math.max(0, ridgeMask);

  // 5. Billowy erosion and domes
  h += (billow(nx * 6.0, nz * 6.0, 4, 73) - 0.5) * 2 * 7.0;

  // 6. Micro-roughness
  h += (fbm(nx * 18.0, nz * 18.0, 3, 101) - 0.5) * 2 * 2.0;

  // 7. Gullies / erosion channels
  const gully = billow(nx * 9.0 + 4.2, nz * 9.0 - 5.1, 3, 117);
  h += (gully - 0.5) * 2 * 4.0 * Math.max(0, ridgeMask);

  return h;
}

// Precomputed road centerline elevations to ensure smooth Catmull-Rom grade
const trackCenterlineElevations = new Float32Array(SPLINE_SAMPLES);
(() => {
  const rawHeights = new Float32Array(SPLINE_SAMPLES);
  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    rawHeights[i] = wildHeight(trackPoints[i].x, trackPoints[i].z);
  }

  // Level the start/finish straightaway so vehicles spawn and line up on a flat grid
  const startH = rawHeights[0];
  const startPadRadius = 40; // ~65 m around the start line
  for (let s = -startPadRadius; s <= startPadRadius; s++) {
    const idx = (s + SPLINE_SAMPLES) % SPLINE_SAMPLES;
    const t = Math.abs(s) / startPadRadius;
    const blend = 1.0 - t * t * (3 - 2 * t);
    rawHeights[idx] = rawHeights[idx] * (1.0 - blend) + startH * blend;
  }

  // Smooth the road elevation along the track using a wide Gaussian filter
  const RADIUS = 24;
  for (let i = 0; i < SPLINE_SAMPLES; i++) {
    let sum = 0, weightSum = 0;
    for (let offset = -RADIUS; offset <= RADIUS; offset++) {
      const idx = (i + offset + SPLINE_SAMPLES) % SPLINE_SAMPLES;
      const w = Math.exp(-(offset * offset) / (2 * 10 * 10));
      sum += rawHeights[idx] * w;
      weightSum += w;
    }
    trackCenterlineElevations[i] = sum / weightSum;
  }
})();

// ---------------------------------------------------------------------------
// Authoritative deterministic heightAt(x, z)
// ---------------------------------------------------------------------------
export function heightAt(x, z) {
  const hWild = wildHeight(x, z);

  // Query road corridor
  const query = trackQuery(x, z);
  const d = query.dist;
  const inner = TERRAIN.innerCorridor;
  const outer = TERRAIN.outerCorridor;

  let h = hWild;

  if (d < outer) {
    // Smoothed centerline elevation
    const roadCenterH = trackCenterlineElevations[query.index];
    const lat = query.lateral;

    // Banking: curves tilt dynamically to support high-speed cornering
    const curvature = trackCurvatures[query.index];
    // Bank angle proportional to curvature, clamped to ~15 degrees
    const bankSlope = clamp(curvature * 14.0, -0.22, 0.22);
    const bankElev = lat * bankSlope;

    // Road crown and wheel ruts: slight crown in middle and subtle ruts
    const absLat = Math.abs(lat);
    const rutFactor = Math.sin(absLat * 0.7) * 0.12;

    const hRoad = roadCenterH + bankElev - rutFactor;

    // Smooth Hermite blend between roadbed and wild mountain terrain
    const tCorridor = hermiteRamp(inner, outer, d);
    h = hRoad * (1.0 - tCorridor) + hWild * tCorridor;
  }

  // Island rim fade to sea/ocean basin
  const r = Math.hypot(x, z);
  if (r > TERRAIN.islandStart) {
    const fade = 1.0 - hermiteRamp(TERRAIN.islandStart, TERRAIN.islandEnd, r);
    h = h * fade - TERRAIN.seaDepth * (1.0 - fade);
  }

  return h;
}

// ---------------------------------------------------------------------------
// Slope and normal sampling
// ---------------------------------------------------------------------------
export function slopeAt(x, z, step = 1.5) {
  const dx = (heightAt(x + step, z) - heightAt(x - step, z)) / (2 * step);
  const dz = (heightAt(x, z + step) - heightAt(x, z - step)) / (2 * step);
  return Math.hypot(dx, dz);
}

export function sampleGround(x, z, step = 1.2) {
  const h = heightAt(x, z);
  const dx = (heightAt(x + step, z) - heightAt(x - step, z)) / (2 * step);
  const dz = (heightAt(x, z + step) - heightAt(x, z - step)) / (2 * step);
  const len = Math.hypot(dx, 1, dz) || 1;
  return {
    height: h,
    normal: {
      x: -dx / len,
      y: 1 / len,
      z: -dz / len
    }
  };
}

export function surfaceAt(x, z, h) {
  const d = trackDistance(x, z);
  if (d <= TERRAIN.roadHalfWidth + 1.2) {
    return SURFACE.ROAD;
  }
  const slope = slopeAt(x, z);
  if (slope > 0.58 || h > 42) {
    return SURFACE.ROCK;
  }
  if (d <= TERRAIN.outerCorridor * 0.8) {
    return SURFACE.DIRT;
  }
  if (h < 5.0) {
    return SURFACE.SAND;
  }
  const noise = fbm(x * 0.015, z * 0.015, 3, 149);
  if (noise > 0.62) {
    return SURFACE.DIRT;
  }
  return SURFACE.GRASS;
}

// ---------------------------------------------------------------------------
// Checkpoints and Start Pose
// ---------------------------------------------------------------------------
export function buildCheckpoints(count = 12) {
  const cps = [];
  const step = Math.floor(SPLINE_SAMPLES / count);
  for (let i = 0; i < count; i++) {
    const idx = (i * step) % SPLINE_SAMPLES;
    const pt = trackPoints[idx];
    const tang = trackTangents[idx];
    const h = heightAt(pt.x, pt.z);
    cps.push({
      index: i,
      x: pt.x,
      z: pt.z,
      height: h + 2.5,
      groundHeight: h,
      tangent: tang,
      yaw: Math.atan2(tang.x, tang.z)
    });
  }
  return cps;
}

export function startPose() {
  const p0 = trackPoints[0];
  const tang = trackTangents[0];
  const yaw = Math.atan2(tang.x, tang.z);
  return {
    x: p0.x,
    z: p0.z,
    y: heightAt(p0.x, p0.z),
    yaw
  };
}
